import DOMPurify from "dompurify";
import React, { useMemo, useState } from "react";
import DateUtility from "./dateUtility";
import { linkifyHtml } from "./linkify";
/**
 * The blocks the three lists of shifts (Gule vagter, Min vagtplan, Byttevagter) are built from.
 * They are chronological lists of shifts that differ only in the information a row carries, so the
 * page head, the month bar, the row and the notes are the same everywhere (the design boards
 * GuleVagter, MinVagtplan and Bytte, and their Bred counterparts).
 */
const ICON_PROPS = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true
};
const OperatorIcon = () => (React.createElement("svg", Object.assign({}, ICON_PROPS, { strokeWidth: 2, className: "bi-sr-icon" }),
    React.createElement("rect", { x: "3", y: "4", width: "18", height: "16", rx: "2" }),
    React.createElement("path", { d: "M7 4v16M17 4v16M3 9h4M3 15h4M17 9h4M17 15h4" })));
const ShopIcon = () => (React.createElement("svg", Object.assign({}, ICON_PROPS, { strokeWidth: 2, className: "bi-sr-icon" }),
    React.createElement("path", { d: "M5 8h14l-1 12H6z" }),
    React.createElement("path", { d: "M9 8V6a3 3 0 0 1 6 0v2" })));
const Chevron = () => (React.createElement("svg", Object.assign({}, ICON_PROPS, { strokeWidth: 1.75, className: "bi-sr-chevron" }),
    React.createElement("path", { d: "m9 6 6 6-6 6" })));
/** The role a shift is worked in, as the icon and the name the boards show. */
export const RoleLabel = ({ role, iconOnly }) => {
    const isOperator = role === "Operator";
    if (iconOnly) {
        return isOperator ? React.createElement(OperatorIcon, null) : React.createElement(ShopIcon, null);
    }
    return (React.createElement("span", { className: "bi-sr-role" },
        isOperator ? React.createElement(OperatorIcon, null) : React.createElement(ShopIcon, null),
        isOperator ? "Operatør" : "Kiosk"));
};
/** Something a yellow shift still lacks: "Mangler operatør" or "Mangler 2 kiosk". */
export const Missing = ({ role, count }) => (React.createElement("span", { className: "bi-sr-role" },
    role === "Operator" ? React.createElement(OperatorIcon, null) : React.createElement(ShopIcon, null),
    role === "Operator" ? "Mangler operatør" : `Mangler ${count !== null && count !== void 0 ? count : 1} kiosk`));
/** "Haster": a red shift, a yellow shift that is close to starting and still not staffed. */
export const UrgentPill = () => (React.createElement("span", { className: "bi-sr-urgent" },
    React.createElement("span", { className: "bi-sr-dot" }),
    "Haster"));
/** "Sat til bytte": the volunteer has put the shift on the exchange. Grey, a state and not a danger. */
export const SwapPill = () => (React.createElement("span", { className: "bi-sr-pill" },
    React.createElement("svg", Object.assign({}, ICON_PROPS, { strokeWidth: 2.25, className: "bi-sr-pill-icon" }),
        React.createElement("path", { d: "M7 4 3 8l4 4M3 8h14M17 12l4 4-4 4M21 16H7" })),
    "Sat til bytte"));
const hasText = (html) => !!html && html.replace(/<[^>]*>|&nbsp;/g, "").trim().length > 0;
/** The planner's notes to a shift: collapsed until asked for, since they can be long. */
const ShiftNotes = ({ notes }) => {
    const [open, setOpen] = useState(false);
    const html = useMemo(() => linkifyHtml(DOMPurify.sanitize(notes)), [notes]);
    return (React.createElement("div", { className: "bi-sr-notes" },
        React.createElement("button", { type: "button", className: "bi-sr-notes-toggle", "aria-expanded": open, onClick: () => setOpen(!open) },
            React.createElement("svg", Object.assign({}, ICON_PROPS, { strokeWidth: 2, className: "bi-sr-notes-icon" }),
                React.createElement("path", { d: "M6 3h9l4 4v14H6z" }),
                React.createElement("path", { d: "M9 12h7M9 16h7" })),
            "Noter til vagten",
            React.createElement("svg", Object.assign({}, ICON_PROPS, { strokeWidth: 2.25, className: "bi-sr-notes-caret" }),
                React.createElement("path", { d: "m6 9 6 6 6-6" }))),
        open ? React.createElement("div", { className: "bi-sr-notes-panel", dangerouslySetInnerHTML: { __html: html } }) : null));
};
const Films = ({ events }) => {
    if (!events || events.length === 0) {
        return (React.createElement("span", { className: "bi-sr-films" },
            React.createElement("span", { className: "bi-sr-nofilm" }, "Ingen planlagte film")));
    }
    return (React.createElement("span", { className: "bi-sr-films" }, events.map((evt, idx) => (React.createElement("span", { className: "bi-sr-film", key: `evt-${idx}` },
        React.createElement("span", { className: "bi-sr-film-time" }, DateUtility.parse(evt.startTime).toFormat("HH:mm")),
        React.createElement("span", { className: "bi-sr-film-title" }, evt.eventContent.title))))));
};
export const ShiftRow = (props) => (React.createElement("div", { className: `bi-sr bi-sr-${props.list}${props.urgent ? " bi-sr-row-urgent" : ""}${props.status ? ` bi-sr-edge bi-sr-edge-${props.status}` : ""}` },
    React.createElement("a", { className: "bi-sr-link", href: props.href },
        React.createElement("span", { className: "bi-sr-main" },
            React.createElement("span", { className: "bi-sr-when" },
                props.status
                    ? React.createElement(React.Fragment, null,
                        React.createElement("span", { className: `bi-sr-statusdot bi-sr-statusdot-${props.status}`, "aria-hidden": "true" }),
                        React.createElement("span", { className: "visually-hidden" }, props.status === "planned" ? "Planlagt" : "Foreløbig"))
                    : null,
                React.createElement("span", { className: "bi-sr-date" }, DateUtility.rowDay(props.start)),
                React.createElement("span", { className: "bi-sr-sep", "aria-hidden": "true" }, " \u00B7 "),
                React.createElement("span", { className: "bi-sr-time" }, DateUtility.rowInterval(props.start, props.end))),
            React.createElement("span", { className: `bi-sr-ctx${props.dots ? " bi-sr-ctx-dots" : ""}` }, props.context),
            React.createElement(Films, { events: props.events })),
        props.chevron === false ? null : React.createElement(Chevron, null)),
    props.actions ? React.createElement("span", { className: "bi-sr-actions" }, props.actions) : null,
    hasText(props.notes) ? React.createElement(ShiftNotes, { notes: props.notes }) : null));
/**
 * The volunteer's own byttevagt in the Byttevagter list (the board's "Din byttevagt" row): tinted,
 * marked, with the share link to copy and the offer to take back. It is not one link like the other
 * rows, since it holds buttons: the date opens the shift, the buttons do the rest.
 */
export const OwnShiftRow = (props) => {
    const [copied, setCopied] = useState(null);
    const copyLink = () => {
        const url = `${window.location.origin}/byttevagt/?slug=${props.slug}`;
        navigator.clipboard.writeText(url)
            .then(() => setCopied("ok"))
            .catch(() => setCopied("failed"))
            .finally(() => window.setTimeout(() => setCopied(null), 2500));
    };
    // On a phone the offers are said in the line of the role; on a wide screen, under the mark.
    const offerText = props.offers === 1 ? "1 tilbud" : `${props.offers} tilbud`;
    const offers = props.offers > 0
        ? React.createElement("a", { className: "bi-sr-offers", href: props.offersHref }, offerText)
        : React.createElement("span", { className: "bi-sr-offers" }, "ingen tilbud endnu");
    return (React.createElement("div", { className: "bi-sr bi-sr-bytte bi-sr-own" },
        React.createElement("div", { className: "bi-sr-link" },
            React.createElement("span", { className: "bi-sr-main" },
                React.createElement("span", { className: "bi-sr-when" },
                    React.createElement("a", { className: "bi-sr-date", href: props.href }, DateUtility.rowDay(props.start)),
                    React.createElement("span", { className: "bi-sr-sep", "aria-hidden": "true" }, " \u00B7 "),
                    React.createElement("a", { className: "bi-sr-time", href: props.href }, DateUtility.rowInterval(props.start, props.end)),
                    React.createElement("span", { className: "bi-sr-own-fra" },
                        React.createElement("span", { className: "bi-sr-own-chip" }, "Din byttevagt"),
                        props.offers > 0 ? React.createElement("a", { className: "bi-sr-own-count", href: props.offersHref }, offerText) : null)),
                React.createElement("span", { className: "bi-sr-ctx bi-sr-ctx-dots" },
                    React.createElement(RoleLabel, { role: props.role }),
                    offers),
                React.createElement(Films, { events: props.events }),
                React.createElement("span", { className: "bi-sr-own-actions" },
                    React.createElement("button", { type: "button", className: "bi-sr-own-copy", onClick: copyLink }, copied === "ok" ? "Kopieret" : copied === "failed" ? "Kunne ikke kopiere" : "Kopiér link"),
                    React.createElement("button", { type: "button", className: "bi-sr-own-cancel", onClick: props.onCancel }, "Tr\u00E6k tilbage"))))));
};
/** Groups items that are already in chronological order by the month of their start. */
export function groupByMonth(items, start) {
    const groups = [];
    items.forEach((item) => {
        const dt = start(item);
        const key = dt.toFormat("yyyy-MM");
        let group = groups[groups.length - 1];
        if (!group || group.key !== key) {
            group = { key, label: DateUtility.monthLabel(dt), items: [] };
            groups.push(group);
        }
        group.items.push(item);
    });
    return groups;
}
/** The rows of a list under their month bars, with the column titles shown from the wide layout. */
export function ShiftList(props) {
    const groups = groupByMonth(props.items, props.start);
    // Month bars say which month the rows are in, which is only worth saying when the rows that
    // are shown are in more than one: a list of one month has the month on every row already.
    const bars = groups.length > 1;
    return (React.createElement("div", { className: `bi-sl bi-sl-${props.list}` },
        React.createElement("div", { className: "bi-sl-columns", "aria-hidden": "true" }, props.columns.map((title, idx) => (React.createElement("span", { key: idx }, title)))),
        groups.map((group) => (React.createElement(React.Fragment, { key: group.key },
            bars ? React.createElement("div", { className: "bi-sl-month" }, group.label) : null,
            group.items.map((item, idx) => (React.createElement(React.Fragment, { key: idx }, props.renderRow(item))))))),
        props.footer));
}
/**
 * The "Antal vagter" of a list block: how many shifts a list shows before "Vis flere", which adds
 * the same number each press. Zero, empty or anything else means the whole list.
 */
export const parseVisibleCount = (value) => {
    const count = parseInt(String(value !== null && value !== void 0 ? value : ""), 10);
    return count > 0 ? count : 0;
};
/** "Vis flere", at the foot of the card of a list that is not shown in full. */
export const MoreButton = ({ onClick }) => (React.createElement("button", { type: "button", className: "bi-sl-more", onClick: onClick },
    React.createElement("svg", { viewBox: "0 0 24 24", width: "16", height: "16", fill: "none", stroke: "currentColor", strokeWidth: "2.25", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true" },
        React.createElement("path", { d: "m6 9 6 6 6-6" })),
    "Vis flere"));
/** The head of a section of the front page: the title, a count, and where "Se alle" goes. */
export const SectionHead = (props) => (React.createElement("div", { className: "bi-sl-section-head" },
    React.createElement("h2", null, props.title),
    props.count !== undefined ? React.createElement("span", { className: "bi-sl-count" }, props.count) : null,
    props.href ? React.createElement("a", { href: props.href }, "Se alle") : null,
    props.children));
/** The head of a list page: the title (with the number of shifts when it is a count), what it is for. */
export const ShiftPageHead = (props) => (React.createElement("div", { className: "bi-sl-head" },
    React.createElement("div", { className: "bi-sl-head-text" },
        React.createElement("div", { className: "bi-sl-title" },
            React.createElement("h1", null, props.title),
            props.count !== undefined ? React.createElement("span", { className: "bi-sl-count" }, props.count) : null),
        props.subtitle ? React.createElement("span", { className: "bi-sl-subtitle" }, props.subtitle) : null),
    props.children));
/** "Show as section" of a list block: the small head and "Se alle" of the front page, or a page. */
export const parseAsSection = (value) => String(value !== null && value !== void 0 ? value : "") === "1";
